import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, Grid, Grid2, IconButton, List, Snackbar, Stack, Tab, Tabs, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import MuiDialogHeader from '../Shared/MuiDialogHeader';
import { DeleteRoleRequest, ModuleType, PermissionDTO, PermissionGateApiClient, PermissionType, RoleDTO, RoleListDTO, UpdateRolePermissionsRequest } from '../Api/ApiServerHooks';
import { useGateApi } from '../Api/useGateApi';
import { Split } from 'ts-react-splitter';
import NoDataPlaceholder from "../Shared/NoDataPlaceholder";
import { Add, AddCircle, Delete, DeleteOutline } from '@mui/icons-material';
import CreatePermissionDialog from './CreatePermissionDialog';
import DeleteDialog from '../Shared/DeleteDialog';
import AccordionCompact from '../Shared/AccordionCompact';
import { isPricingDatabasePermission } from './pricingDatabasePermissions';
import { getPermissionModuleType } from './sumecPermissions';


export interface PermissionsSelectDetailDialogProps {
    open: boolean;
    selectedValue?: RoleListDTO;
    tenantId: number;
    onClose: () => void;
}

export default function PermissionsSelectDetailDialog(props: Readonly<PermissionsSelectDetailDialogProps>) {
    const { open, selectedValue, tenantId, onClose } = props;
    const theme = useTheme();
    const biggerThan700 = useMediaQuery(theme.breakpoints.up(700));
    const { t } = useTranslation();
    const [roleDetail, setRoleDetail] = useState<RoleDTO | null>(null);
    const [roleDelete, setRoleDelete] = useState<RoleDTO | null>(null);
    const [roles, setRoles] = useState<RoleListDTO[] | null>(null);
    const [selectedRole, setSelectedRole] = useState<RoleListDTO | null>(null);
    const [splitPercent, setSplitPercent] = useState<number>(biggerThan700 ? 30 : 50);
    const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);
    const [saveConfirm, setSaveConfirm] = useState(false);

    const { authorizedApi } = useGateApi(PermissionGateApiClient);

    useEffect(() => {
        setSelectedRole(null);
        if (open && tenantId) {
            authorizedApi?.getRoles(tenantId).then((res) => {
                if (res.roles) setRoles(res.roles);
                else setRoles([]);
            });
        }
        else 
            setRoles(null);
    }, [open, tenantId]);

    useEffect(() => {
        if (selectedRole?.id) {
            authorizedApi?.getRolePermissions(selectedRole.id).then((res) => {
                if (res.role) setRoleDetail(res.role);
                else setRoleDetail(null);
            });
        }
        else setRoleDetail(null);
    }, [selectedRole]);

    useEffect(() => {
        if (selectedValue) {
            setSelectedRole(selectedValue);
        }
    }, [selectedValue]);

    const handleClose = () => {
        onClose();
    };

    const [value, setValue] = useState('one');

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    const moduleTypes = Object.values(ModuleType).filter(v => typeof v === 'number') as ModuleType[];

    const renderPermissionCheckbox = (permission: PermissionDTO, index: number) => (
        <FormControlLabel
            key={permission.id ?? `${permission.name}_${index}`}
            label={t("Permissions." + (permission.name ?? ''))}
            control={
                <Checkbox
                    checked={permission.granted ?? false}
                    onChange={(_, checked) => {
                        if (permission.id === undefined) return;

                        setRoleDetail(previousRole => {
                            if (!previousRole?.permissions) return previousRole;

                            const updatedRole = new RoleDTO(previousRole);
                            updatedRole.permissions = previousRole.permissions.map(previousPermission => {
                                if (previousPermission.id !== permission.id) return previousPermission;

                                const updatedPermission = new (previousPermission.constructor as any)(previousPermission);
                                updatedPermission.granted = checked;
                                return updatedPermission;
                            });
                            return updatedRole;
                        });
                    }}
                />
            }
        />
    );

    return (
        <Dialog
            sx={{ zIndex: 5001}}
            fullWidth   
            maxWidth={false}
            open={open}
            onClose={(e) => { handleClose() }}
            aria-labelledby="selector-perm-dialog-title"
            aria-describedby="selector-perm-dialog-description"
        >
            <MuiDialogHeader title={t('Administration.userRoleDetail')} onClose={() => handleClose()} />
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>                
                <Split percent={splitPercent} setPercent={(e) => { if (e) setSplitPercent(e); }} renderSplitter={({ dragging }) => (
                    <div
                        style={{
                            marginLeft: '3px',
                            width: '1px',
                            height: '100%',
                            background: dragging ? 'blue' : 'lightgray',
                            cursor: 'col-resize',
                        }}
                    />
                )} >
                    <Box p={1}
                        sx={{
                            height: '70vh',
                            overflow: 'auto'
                        }}>
                        {biggerThan700 && <Button variant="outlined" startIcon={<AddCircle />} color='primary' onClick={() => { setCreateDialogOpen(true); }} sx={{ height: "38px", fontSize: '12px', fontWeight: 'bold', boxShadow: 3, caretColor: 'transparent' }} >
                            {t('Administration.createRole')}
                        </Button>}                        
                        {!biggerThan700 && <IconButton size='small' color='primary' onClick={() => { setCreateDialogOpen(true); }} sx={{ boxShadow: 3 }}>
                            <AddCircle />
                        </IconButton>}
                        <List>
                            {roles?.map((role, i) => (
                                <Box key={role.id + '_' + i} sx={{ padding: '4px', backgroundColor: selectedRole?.id === role.id ? 'lightgray' : 'transparent', cursor: 'pointer', boxShadow: 1 }} display={'flex'} justifyContent={'space-between'} onClick={(e) => { setSelectedRole(role); }}>
                                    <Box pl={1}>
                                        <Typography mt={1}>{role.name}</Typography>
                                    </Box>
                                    <Box>
                                        <IconButton size='small' onClick={(e) => {
                                            setRoleDelete(role);
                                            setDeleteDialogOpen(true);
                                        }}>
                                            <DeleteOutline color='primary' />
                                        </IconButton>
                                    </Box>

                                </Box>
                            ))}
                        </List>
                    </Box>
                    {selectedRole && <Box
                        sx={{
                            height: '70vh',
                            overflow: 'auto'
                        }}>
                        <Box p={2}>
                            <TextField 
                                label={t('Name')}
                                value={roleDetail?.name || ''}
                                onChange={(e) => {
                                    setRoleDetail(prev => {
                                        if (!prev) return prev;
                                        const updatedRole = new RoleDTO(prev);
                                        updatedRole.name = e.target.value;
                                        return updatedRole;
                                    });
                                }}
                            />
                        </Box>
                        <Box p={1} pl={2}>
                            <Box display={'flex'} justifyContent={'start'} gap={4} alignItems={'center'} mb={2}>
                                <Typography variant='h5'>{t('Administration.permissions')}</Typography>                           
                                <Button disabled={!roleDetail || updating} variant='contained' onClick={(e) => { 
                                    if (roleDetail) {
                                        setUpdating(true);
                                        authorizedApi?.updateRolePermissions(new UpdateRolePermissionsRequest({role: roleDetail})).then((res) => {
                                            if (res.ok) {
                                                setSaveConfirm(true);
                                                setRoles(prev => {
                                                    if (!prev) return prev;
                                                    return prev.map(r => {
                                                        if (r.id === roleDetail.id) {
                                                            const updatedRole = new RoleListDTO(r);
                                                            updatedRole.name = roleDetail.name;
                                                            return updatedRole;
                                                        }
                                                        return r;
                                                    });
                                                });
                                            }
                                        }).finally(() => {
                                            setUpdating(false);
                                        });
                                    }
                                }}>{t('Save')}</Button>
                            </Box>                       
                            <Box>
                                {roleDetail?.permissions?.some(permission => isPricingDatabasePermission(permission.permissionType)) && (
                                    <Box sx={{ mt: 2 }}>
                                        <AccordionCompact
                                            defaultExpanded={false}
                                            onExpandCollapse={() => { }}
                                            heading={
                                                <Stack alignItems='center' spacing={1} direction='row'>
                                                    <Typography variant='h6'>{t('PermissionGroups.PricingDatabases')}</Typography>
                                                </Stack>
                                            }>
                                            <Stack alignItems='flex-start'>
                                                {roleDetail.permissions
                                                    .filter(permission => isPricingDatabasePermission(permission.permissionType))
                                                    .map(renderPermissionCheckbox)}
                                            </Stack>
                                        </AccordionCompact>
                                    </Box>
                                )}
                                {moduleTypes.map((moduleType) => (
                                    roleDetail?.permissions?.some(perm => getPermissionModuleType(perm) === moduleType
                                        && !isPricingDatabasePermission(perm.permissionType)) && (
                                    <Box key={moduleType} sx={{ mt: 2 }}>
                                        <AccordionCompact
                                            defaultExpanded={false}
                                            onExpandCollapse={() => { }}
                                            heading={
                                                <Stack alignItems={'center'} spacing={1} direction='row'>
                                                    <Typography variant='h6'>{t("ModuleType." + ModuleType[moduleType])}</Typography>
                                                </Stack>
                                            }>
                                            <Box>
                                                {moduleType !== ModuleType.BimComplete && roleDetail?.permissions
                                                    ?.filter(permission => getPermissionModuleType(permission) === moduleType
                                                        && !isPricingDatabasePermission(permission.permissionType))
                                                    .map(renderPermissionCheckbox)}
                                                {moduleType === ModuleType.BimComplete && 
                                                    <Box>
                                                        <Box mb={2}>
                                                            <Typography variant='body1' fontSize={18} fontStyle={'italic'}>{t("ModuleType.ImportAndConnectproperties")}</Typography>
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.ImportStandardBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.ImportStandardBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.ImportStandardBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.AddPropertiesBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.AddPropertiesBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.AddPropertiesBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                        </Box>
                                                        <Box mb={2}>
                                                            <Typography variant='body1' fontSize={18} fontStyle={'italic'}>{t("ModuleType.MapEntityToDataStandard")}</Typography>
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.CreateMappingBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.CreateMappingBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.CreateMappingBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.EditMappingBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.EditMappingBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.EditMappingBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteMappingBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteMappingBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteMappingBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                        </Box>
                                                        
                                                        <Box mb={2}>
                                                            <Typography variant='body1' fontSize={18} fontStyle={'italic'}>{t("ModuleType.routeAdministration")}</Typography>
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.CreateRouteBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.CreateRouteBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.CreateRouteBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.EditRouteBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.EditRouteBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.EditRouteBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteRouteBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteRouteBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteRouteBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                        </Box>
                                                        
                                                        <Box mb={2}>
                                                            <Typography variant='body1' fontSize={18} fontStyle={'italic'}>{t("ModuleType.addNonGraphicInfo")}</Typography>
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.UpdateElementFromProjectBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.UpdateElementFromProjectBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.UpdateElementFromProjectBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteElementPropertiesBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteElementPropertiesBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.DeleteElementPropertiesBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                            <FormControlLabel 
                                                                    label={t("Permissions." + (roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.ImportExcelBimComplete)?.name ?? ''))} 
                                                                    control={
                                                                    <Checkbox 
                                                                        checked={roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.ImportExcelBimComplete)?.granted ?? false} 
                                                                        onChange={(e, ch) => { 
                                                                            setRoleDetail(prev => {
                                                                                if (!prev?.permissions) return prev;
                                                                                const updatedRole = new RoleDTO(prev);
                                                                                updatedRole.permissions = prev.permissions.map(p => {
                                                                                    if (p.id === roleDetail?.permissions?.find(perm => perm.permissionType === PermissionType.ImportExcelBimComplete)?.id) {
                                                                                        const updatedPerm = new (p.constructor as any)(p);
                                                                                        updatedPerm.granted = ch;
                                                                                        return updatedPerm;
                                                                                    }
                                                                                    return p;
                                                                                });
                                                                                return updatedRole;
                                                                            });
                                                                        }} 
                                                                    />
                                                                } 
                                                            />
                                                        </Box>
                                                    </Box>
                                                }
                                            </Box>
                                        </AccordionCompact >
                                    </Box>
                                )))}                                
                            </Box>
                        </Box>
                        <Box p={1} pl={2}>
                            <Box>
                                <Typography variant='h5'>{t('Administration.dataStandards')}</Typography>
                            </Box>
                            <Grid2 container spacing={1}>                                
                                {roleDetail?.dataStandardPermissions?.map((perm, i) => (
                                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={perm.name + '_' + i}>
                                        <FormControlLabel 
                                            key={perm.name + '_' + i}
                                            label={perm.name} 
                                            sx={{ 
                                                '& .MuiFormControlLabel-label': { 
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere'
                                                } 
                                            }}
                                            control={
                                                <Checkbox 
                                                    checked={perm.granted} 
                                                    onChange={(e, ch) => { 
                                                        setRoleDetail(prev => {
                                                            if (!prev?.dataStandardPermissions) return prev;
                                                            const updatedRole = new RoleDTO(prev);
                                                            updatedRole.dataStandardPermissions = prev.dataStandardPermissions.map(p => {
                                                                if (p.id === perm.id) {
                                                                    const updatedPerm = new (p.constructor as any)(p);
                                                                    updatedPerm.granted = ch;
                                                                    return updatedPerm;
                                                                }
                                                                return p;
                                                            });
                                                            return updatedRole;
                                                        });
                                                    }} 
                                                />
                                            } 
                                        />
                                    </Grid2>
                                ))}
                            </Grid2>
                        </Box>                        
                        <Box p={1} pl={2}>
                            <Box>
                                <Typography variant='h5'>{t('Administration.pricingDatabases')}</Typography>
                            </Box>
                            <Grid2 container spacing={1}>
                                {roleDetail?.pricingDatabasePermissions?.map((perm, i) => (
                                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={perm.name + '_' + i}>
                                        <FormControlLabel
                                            key={perm.name + '_' + i}
                                            // A database shared in from another tenant is named after its owner, so it
                                            // is not mistaken for one of this tenant's own.
                                            label={perm.isShared && perm.ownerTenantName
                                                ? `${perm.name} (${perm.ownerTenantName})`
                                                : perm.name}
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere'
                                                }
                                            }}
                                            control={
                                                <Checkbox
                                                    checked={perm.granted}
                                                    onChange={(e, ch) => {
                                                        setRoleDetail(prev => {
                                                            if (!prev?.pricingDatabasePermissions) return prev;
                                                            const updatedRole = new RoleDTO(prev);
                                                            updatedRole.pricingDatabasePermissions = prev.pricingDatabasePermissions.map(p => {
                                                                if (p.id === perm.id) {
                                                                    const updatedPerm = new (p.constructor as any)(p);
                                                                    updatedPerm.granted = ch;
                                                                    return updatedPerm;
                                                                }
                                                                return p;
                                                            });
                                                            return updatedRole;
                                                        });
                                                    }}
                                                />
                                            }
                                        />
                                    </Grid2>
                                ))}
                            </Grid2>
                        </Box>
                    </Box>}
                    {!selectedRole && <Box
                        sx={{
                            height: '70vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <NoDataPlaceholder title={t('Administration.noRoleSelected')} paragraph=''/>
                    </Box>}
                </Split>
                <CreatePermissionDialog tenantId={tenantId} isOpen={createDialogOpen} onClose={(r) => { 
                    if (r) 
                        setRoles(prev => prev ? [...prev, r] : [r]); 
                    setCreateDialogOpen(false); 
                }} />
                <DeleteDialog data={roleDelete} open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} onDeleteAgree={(role) => {
                    authorizedApi?.deleteRole(new DeleteRoleRequest({ id: role.id})).then((res) => {
                        if (res.success) {
                            setRoles(prev => prev?.filter(r => r.id !== role.id) ?? []);
                            if (selectedRole?.id === role.id) setSelectedRole(null);
                        }
                        setDeleteDialogOpen(false);
                    });
                }} />
                <Snackbar open={saveConfirm} autoHideDuration={3000} onClose={() => setSaveConfirm(false)}>
                    <Alert onClose={() => setSaveConfirm(false)} severity="success" sx={{ width: '100%' }}>
                        {t('Saved')}
                    </Alert>
                </Snackbar>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={(e) => { handleClose() }} autoFocus>{t('Close')}</Button>
            </DialogActions>
        </Dialog>
    )
}
